import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField } from '@munifocus/ui';
import type { ContactLinkedFile } from '@munifocus/contracts-gov';
import { useContactFileLookup } from '../../lib/front-desk-api';

/**
 * Search files by number, address, owner or PID and pick one or more. A file
 * the clerk may see only as the public does comes back `redacted` and is
 * matched on number and address alone — the server applies that.
 */
export function FileLinkPicker({
  localGovernmentId,
  value,
  onChange,
  multiple = true,
  label,
}: {
  localGovernmentId: string | null;
  value: ContactLinkedFile[];
  onChange: (files: ContactLinkedFile[]) => void;
  multiple?: boolean;
  label?: string;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const lookup = useContactFileLookup(localGovernmentId, input);
  const options = lookup.data?.results ?? [];

  return (
    <Autocomplete<ContactLinkedFile, boolean>
      multiple={multiple}
      size="small"
      options={options}
      value={multiple ? value : (value[0] ?? null)}
      filterOptions={(o) => o}
      loading={lookup.isFetching}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => `${o.reference} — ${o.address}`}
      onInputChange={(_e, v) => setInput(v)}
      onChange={(_e, v) => {
        if (Array.isArray(v)) onChange(v);
        else onChange(v ? [v] : []);
      }}
      noOptionsText={
        input.trim().length < 2
          ? t('gov.frontDesk.lookup.typeMore')
          : t('gov.frontDesk.lookup.none')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('gov.frontDesk.form.linkedFiles')}
          placeholder={t('gov.frontDesk.lookup.placeholder')}
        />
      )}
    />
  );
}
